/**
 * Configuration for the AI form field autogeneration feature.
 *
 * This file owns:
 * - Allowlists of associatedVariable strings to send to the /api/formfill endpoint
 * - Per-field prompt descriptions (used as the `description` hint per field in the request)
 * - Time zone descriptions (differ by assistant type)
 * - Day-of-week description builder (shared format for both assistant types)
 * - Default top-level prompts shown in the Autofill Prompt Settings section
 *
 * Labels and tips are pulled dynamically from the live template module fields at call time —
 * only the associatedVariable strings and prompt text are static here.
 *
 * To add a new field: add its associatedVariable to the appropriate allowlist Set,
 * then add an entry in FORM_FILL_FIELD_DESCRIPTIONS (or handle it in resolveAiAutofillConfig
 * for day-of-week / time zone special cases).
 */

// ─── Allowlists ───────────────────────────────────────────────────────────────
// These are the associatedVariable values sent to the AI formfill endpoint.
// {OfficeWebsite} is intentionally excluded — it is populated directly from websiteUrl.
// {OfficeEmailAddressTextMessage} is intentionally excluded.

export const AI_ORDERING_FORM_FILL_VARIABLES = new Set<string>([
    '{LocationName}',
    '{Address}',
    '{SHARED_OTHER_QUESTION_INFO}',
    '{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}',
    '{Webhook-SearchAddress-Latitude}',
    '{Webhook-SearchAddress-Longitude}',
    'DISABLED_DAYS',
    '{MONDAY}',
    '{TUESDAY}',
    '{WEDNESDAY}',
    '{THURSDAY}',
    '{FRIDAY}',
    '{SATURDAY}',
    '{SUNDAY}',
    '{TIME_ZONE}',
    '{Webhook-Time-Zone}',
]);

export const DENTAL_FORM_FILL_VARIABLES = new Set<string>([
    '{ClientName}',
    '{TransferTarget}',
    '{EmergencyTransferTarget}',
    '{OfficeGoogleMapsLink}',
    '{MONDAY}',
    '{TUESDAY}',
    '{WEDNESDAY}',
    '{THURSDAY}',
    '{FRIDAY}',
    '{SATURDAY}',
    '{SUNDAY}',
    'DISABLED_DAYS',
    '{TIME_ZONE}',
    '{AboutThePracticePrompt}',
    '{Services}',
    '{BracesInquiries}',
    '{PediatricInquiriesResponse}',
    '{PaymentPlansPrompt}',
    '{AcceptingNewPatientsResponse}',
    '{WalkInsMessage}',
    '{OfficeEmail}',
]);

export const FORM_FILL_VARIABLES_BY_ASSISTANT_TYPE: Record<string, Set<string>> = {
    'ai-ordering': AI_ORDERING_FORM_FILL_VARIABLES,
    'dental-scheduling': DENTAL_FORM_FILL_VARIABLES,
};

// ─── Day-of-week ──────────────────────────────────────────────────────────────
// Same output format for both assistant types: 24-hour HH:MM,HH:MM
// {websiteUrl} is replaced at call time with the actual URL.

export const DAY_KEYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

export const dayHoursDescription = (day: string): string =>
    `Look up our ${day} business hours from {websiteUrl} and return them in the required format.
Output format rules:
* Return ONLY the hours value.
* Use 24-hour time.
* Format must strictly be: HH:MM,HH:MM
* If there are multiple open periods, separate them with a pipe: HH:MM,HH:MM|HH:MM,HH:MM
* For closed days, output exactly: 00:00,00:01
* Do not include labels, explanations, timezone text, or additional formatting.
* Only use hours that are listed on the website.
Examples: 08:00,17:00 | 08:00,12:00|13:00,17:00 | 00:00,00:01
If no details are found, return exactly: Unknown`;

// ─── Time zone descriptions (differ by assistant type) ────────────────────────
// {websiteUrl} is replaced at call time.

export const TIME_ZONE_DESCRIPTIONS: Record<string, string> = {
    'ai-ordering':
        `Look up our address from {websiteUrl} and decipher the time zone from the location, then return it in the required format.
Output format rules:
* Return only one of: US/Eastern, US/Central, US/Mountain, US/Pacific
* Do not include explanations or additional text.
If no details are found, return exactly: Unknown`,

    'dental-scheduling':
        `Look up our office's address from {websiteUrl} and decipher the time zone from the location, then return it in the required format.
Output format rules:
* Return only one of: US/Eastern, US/Central, US/Mountain, US/Pacific, US/Hawaii
* Do not include explanations or additional text.
If no details are found, return exactly: Unknown`,
};

// ─── Per-field prompt descriptions ───────────────────────────────────────────
// Keyed by associatedVariable string.
// {websiteUrl} placeholder is replaced at call time with the actual URL.
// Fields not present in this map are excluded from the API request (no description = skip).
// Day-of-week fields and time zone fields are handled separately above.

export const SH_LOCATION_NAME_PROMPT = `Visit {websiteUrl} and identify the restaurant name for this location.
Return the result as the restaurant name exactly as it is identified on the website.
Examples:
Happy's Pizza
Papa Murphy's
Output format rules:
* Return ONLY the restaurant name.
* Use the location-specific restaurant name from the website.
* Do not include the city, state, address, labels, explanations, or additional text.
* If the restaurant name cannot be determined, return exactly: Unknown`;

export const LOC_LOCATION_NAME_PROMPT = `Visit {websiteUrl} and identify the restaurant name and city for this location.
Return the result in the format: <Restaurant Name> in <City>
Examples:
Happy's Pizza in Dearborn
Papa Murphy's in Salem
Output format rules:
* Return ONLY the formatted value.
* Use the location-specific restaurant name and city from the website.
* Do not include the state, address, labels, explanations, or additional text.
* If either the restaurant name or city cannot be determined, return exactly: Unknown`;

export const SH_CLIENT_NAME_PROMPT = `Visit {websiteUrl} and identify the dental practice name for this location.
Return the result as the practice name exactly as it is identified on the website.
Examples:
Smile Culture Dental
Pleasantburg Family Dental
Output format rules:
* Return ONLY the practice name.
* Use the location-specific practice name from the website.
* Do not include the city, state, address, labels, explanations, or additional text.
* If the practice name cannot be determined, return exactly: Unknown`;

export const LOC_CLIENT_NAME_PROMPT = `Visit {websiteUrl} and identify the dental practice name and city for this location.
Return the result in the format: <Practice Name> in <City>
Examples:
Smile Culture Dental in Glen Mills
Pleasantburg Family Dental in Greenville
Output format rules:
* Return ONLY the formatted value.
* Use the location-specific practice name and city from the website.
* Do not include the state, address, labels, explanations, or additional text.
* If either the practice name or city cannot be determined, return exactly: Unknown`;

export const FORM_FILL_FIELD_DESCRIPTIONS: Record<string, string> = {

    '{LocationName}': SH_LOCATION_NAME_PROMPT,

    '{ClientName}': SH_CLIENT_NAME_PROMPT,

    // ── Shared (ai-ordering + dental-scheduling) ──────────────────────────────

    'DISABLED_DAYS':
        `Look up our announced office closures, holiday closures, vacation shutdowns, and special closed dates from {websiteUrl}.
Only include dates where we're explicitly stated to be closed or operating with modified closed hours.
Output format rules:
* Return ONLY a comma-separated list.
* Date format must strictly be: MM/DD/YYYY
* For partial-day closures, append hours in parentheses using 24-hour time: MM/DD/YYYY(HH:MM-HH:MM)
* Use pipes for multiple closed periods on the same date: MM/DD/YYYY(HH:MM-HH:MM|HH:MM-HH:MM)
* Expand date ranges into individual dates.
* Do not include spaces, labels, explanations, or extra text.
Examples: 01/02/2026 | 01/02/2026,01/05/2026(13:00-16:30) | 12/24/2026(08:00-12:00|14:00-17:00)
If no closure dates are found, return exactly: none`,

    // ── AI Ordering ───────────────────────────────────────────────────────────

    '{Address}':
        `Look up the street address for this restaurant location from {websiteUrl} and return it in the required format.
Output format rules:
* Return ONLY the complete mailing address, including street number and street name, city, state abbreviation, and ZIP code.
* Include a suite or unit number when listed.
* Do not include the restaurant name, phone number, directions, labels, explanations, or additional text.
If no address is found, return exactly: Unknown`,

    '{SHARED_OTHER_QUESTION_INFO}':
        `Using information from {websiteUrl}, write a summary in first-person plural (using "we" and "our") as if speaking on behalf of the business. Include helpful non-ordering information that applies across all locations, such as dress code, pet policy, reservation or seating policies, accessibility, company-wide parking policies, accepted payment policies, gift cards, loyalty programs, or other shared FAQs. Do not include location-specific details like individual addresses, hours, phone numbers, directions, or amenities that only apply to one location. If you mention any rewards, promotions, coupons, loyalty programs, or discounts, you must refer the user to the website and recite the web address {websiteUrl} for more information (do not include "https://" or any protocol, and format it so it is easy to speak out loud, like "www.restaurant.com"). Use contractions and natural spoken language. Do not start with a greeting or salutation.
Output as a complete sentence, for example: "We keep the dress code casual, welcome leashed dogs on our patios, and recommend reservations for large parties."
Only include details that are on the website and clearly apply to all locations. If information varies by location, do not include it.
If no details are found, return exactly: Unknown`,

    '{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}':
        `Using information from {websiteUrl}, write a summary in first-person plural (using "we" and "our") as if speaking on behalf of this location. Include the location's hours, address, and answers to any commonly asked questions like parking or whether they're pet friendly. Use contractions and natural spoken language. Do not start with a greeting or salutation.
Output as a complete sentence, for example: "We're open Sunday through Thursday from 11am to 9pm, and we're closed Friday and Saturday. Our address is 100 Main Street in Ballston, New York."
Only include details that are on the website.
If no details are found, return exactly: Unknown`,

    '{Webhook-SearchAddress-Latitude}':
        `Look up our business address from {websiteUrl} and return only the latitude in decimal numeric format.
Do not include labels, explanations, or extra text.
If no details are found, return exactly: Unknown`,

    '{Webhook-SearchAddress-Longitude}':
        `Look up our business address from {websiteUrl} and return only the longitude in decimal numeric format.
Do not include labels, explanations, or extra text.
If no details are found, return exactly: Unknown`,

    // ── Dental Scheduling ─────────────────────────────────────────────────────

    '{TransferTarget}':
        `Look up our primary public phone number from {websiteUrl}.
Output format rules:
* Return ONLY the phone number.
* Format must strictly be 11 digits starting with 1.
* Remove all spaces, parentheses, hyphens, periods, and extensions.
* Prefer the main office phone number listed on the contact page or website header/footer.
* Do not include labels, explanations, or additional text.
Example: 15551234567
If no details are found, return exactly: Unknown`,

    '{EmergencyTransferTarget}':
        `Look up our emergency phone number, after-hours emergency line, or emergency contact instructions from {websiteUrl}.
Output format rules:
* Return ONLY the emergency phone number.
* Format must strictly be 11 digits starting with 1.
* Remove all spaces, parentheses, hyphens, periods, and extensions.
* Do not include labels, explanations, or additional text.
Only return a number if the website explicitly lists a dedicated emergency or after-hours contact number.
Example: 15551234567
If no details are found, return exactly: Unknown`,

    '{OfficeGoogleMapsLink}':
        `Look up our official Google Maps listing or directions link.
Output format rules:
* Return ONLY the Google Maps URL.
* Do not include labels, explanations, or extra text.
* Prefer direct Google Maps business listing links.
Example: https://maps.google.com/?cid=123456789
If no details are found, return exactly: Unknown`,

    '{AboutThePracticePrompt}':
        `Using information from {websiteUrl}, write a short description in first-person plural (using "we" and "our") as if speaking on behalf of the dental office. Cover the office's background, values, team, and what patients can expect. Use contractions and a warm, conversational tone. Do not start with a greeting or salutation.
Only include details that are on the website. Don't make anything up.
If no details are found, return exactly: Unknown`,

    '{Services}':
        `Using information from {websiteUrl}, write a description in first-person plural (using "we" and "our") as if speaking on behalf of the dental office covering the pediatric services we offer. Mention only the pediatric services listed on the website, such as children's cleanings, exams, preventive care, or any pediatric specialties. Use contractions and natural spoken language. Do not start with yes, no, or a greeting.
If no details are found, return exactly: Unknown`,

    '{BracesInquiries}':
        `Using information from {websiteUrl}, write a description in first-person plural (using "we" and "our") as if speaking on behalf of the dental office covering the orthodontic and braces services we offer. Mention only what's listed on the website, like traditional braces, Invisalign, retainers, or orthodontic consultations. Use contractions and a conversational tone. Do not start with yes, no, or a greeting.
Don't mention services that aren't clearly listed.
If no details are found, return exactly: Unknown`,

    '{PediatricInquiriesResponse}':
        `Using information from {websiteUrl}, write a description in first-person plural (using "we" and "our") as if speaking on behalf of the dental office covering whether and how we see kids. Mention only the pediatric services listed on the website, such as children's cleanings, exams, preventive care, sedation options, or pediatric specialties. Use contractions and natural spoken language. Do not start with yes, no, or a greeting.
Don't assume any pediatric services unless they're clearly stated.
If no details are found, return exactly: Unknown`,

    '{PaymentPlansPrompt}':
        `Using information from {websiteUrl}, write a description in first-person plural (using "we" and "our") as if speaking on behalf of the dental office covering the payment options available. Cover payment plans, financing, insurance accepted, or flexible arrangements — but only what's mentioned on the website. Use contractions and natural spoken language. Do not start with yes, no, or a greeting.
If no details are found, return exactly: Unknown`,

    '{AcceptingNewPatientsResponse}':
        `Using information from {websiteUrl}, write a statement in first-person plural (using "we" and "our") as if speaking on behalf of the dental office describing whether we're currently accepting new patients. Use contractions and a warm, conversational tone. Do not start with yes, no, or a greeting.
Only include what's clearly on the website. Don't infer availability from scheduling forms or generic contact pages.
Example: "We're currently welcoming new patients and would love to have you join our practice!"
If no details are found, return exactly: Unknown`,

    '{WalkInsMessage}':
        `Using information from {websiteUrl}, write a statement in first-person plural (using "we" and "our") as if speaking on behalf of the dental office describing our walk-in and same-day appointment policy. Use contractions and natural spoken language. Do not start with yes, no, or a greeting.
Only include details that are on the website.
If no details are found, return exactly: Unknown`,

    '{OfficeEmail}':
        `Look up our email address from {websiteUrl}.
Output format rules:
* Return ONLY the email address.
* Do not include labels, explanations, or extra text.
* Prefer general contact emails such as: info@, frontdesk@, hello@, or office@
Example: info@exampledental.com
If no details are found, return exactly: Unknown`,
};

// ─── Human-readable labels for each variable ─────────────────────────────────
// Used as the section title above each editable prompt textarea in the UI.

export const FORM_FILL_FIELD_LABELS: Record<string, string> = {
    // Shared
    'DISABLED_DAYS':                           'Closed / Holiday Dates',
    '{MONDAY}':                                'Monday Hours',
    '{TUESDAY}':                               'Tuesday Hours',
    '{WEDNESDAY}':                             'Wednesday Hours',
    '{THURSDAY}':                              'Thursday Hours',
    '{FRIDAY}':                                'Friday Hours',
    '{SATURDAY}':                              'Saturday Hours',
    '{SUNDAY}':                                'Sunday Hours',
    '{TIME_ZONE}':                             'Time Zone',
    '{Webhook-Time-Zone}':                     'Webhook Time Zone',
    // AI Ordering
    '{LocationName}':                          'Restaurant Location Name',
    '{Address}':                               'Store Address',
    '{SHARED_OTHER_QUESTION_INFO}':            'Shared Non-Ordering Info',
    '{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}': 'Location Non-Ordering Info',
    '{Webhook-SearchAddress-Latitude}':        'Location Latitude',
    '{Webhook-SearchAddress-Longitude}':       'Location Longitude',
    // Dental Scheduling
    '{ClientName}':                            'Office Name',
    '{TransferTarget}':                        'Transfer Target',
    '{EmergencyTransferTarget}':               'Emergency Transfer Target',
    '{OfficeGoogleMapsLink}':                  'Google Maps Link',
    '{AboutThePracticePrompt}':                'About the Practice',
    '{Services}':                              'Services',
    '{BracesInquiries}':                       'Braces Inquiries',
    '{PediatricInquiriesResponse}':            'Pediatric Inquiries',
    '{PaymentPlansPrompt}':                    'Payment Plans',
    '{AcceptingNewPatientsResponse}':          'Accepting New Patients',
    '{WalkInsMessage}':                        'Walk-In Policy',
    '{OfficeEmail}':                           'Office Email',
};

// ─── Default per-field prompts ────────────────────────────────────────────────
// Returns a map of variable → default prompt for a given assistant type.
// Day-of-week and time zone entries are generated programmatically.
// Used to pre-populate autofillPrompts when the user selects an assistant type.

export const getDefaultAutofillPrompts = (assistantType: string): Record<string, string> => {
    const variables = FORM_FILL_VARIABLES_BY_ASSISTANT_TYPE[assistantType];
    if (!variables) return {};

    const prompts: Record<string, string> = {};
    for (const variable of variables) {
        const dayKey = DAY_KEYS.find(d => variable === `{${d}}`);
        if (dayKey) {
            prompts[variable] = dayHoursDescription(dayKey);
        } else if (variable === '{TIME_ZONE}' || variable === '{Webhook-Time-Zone}') {
            prompts[variable] = TIME_ZONE_DESCRIPTIONS[assistantType] ?? '';
        } else if (FORM_FILL_FIELD_DESCRIPTIONS[variable]) {
            prompts[variable] = FORM_FILL_FIELD_DESCRIPTIONS[variable];
        }
    }
    return prompts;
};
